import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const initialFormData = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  phone: '',
  color: '#3b82f6',
  hourly_rate: '',
  cin_number: '',
  home_address: '',
  payment_type: 'hourly',
  percentage_rate: '',
  role: 'worker',
};

export function AddWorkerDialog({ open, onOpenChange, onSuccess }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(initialFormData);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        ...formData,
        skills: [],
        hourly_rate: formData.payment_type === 'hourly' && formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        percentage_rate: formData.payment_type === 'percentage' && formData.percentage_rate ? parseFloat(formData.percentage_rate) : null,
      };
      
      await api.post('/workers', payload);
      onSuccess();
      onOpenChange(false);
      setFormData(initialFormData);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create worker');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('workers.addWorker')}</DialogTitle>
          <DialogDescription>{t('workers.subtitle')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}
            
            {/* Role selector */}
            <div className="space-y-2">
              <Label>{t('workers.role')}</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({...formData, role: 'worker'})}
                  className={cn(
                    "p-3 rounded-lg border text-left transition-all",
                    formData.role === 'worker'
                      ? "border-accent bg-accent/10 ring-1 ring-accent"
                      : "border-border hover:border-accent/50"
                  )}
                  data-testid="role-worker"
                >
                  <p className="font-medium text-sm">{t('workers.roleWorker')}</p>
                  <p className="text-xs text-muted-foreground">{t('workers.roleWorkerDesc')}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, role: 'manager'})}
                  className={cn(
                    "p-3 rounded-lg border text-left transition-all",
                    formData.role === 'manager'
                      ? "border-accent bg-accent/10 ring-1 ring-accent"
                      : "border-border hover:border-accent/50"
                  )}
                  data-testid="role-manager"
                >
                  <p className="font-medium text-sm">{t('workers.roleManager')}</p>
                  <p className="text-xs text-muted-foreground">{t('workers.roleManagerDesc')}</p>
                </button>
              </div>
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">{t('workers.firstName')} *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  required
                  data-testid="worker-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">{t('workers.lastName')} *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  required
                  data-testid="worker-last-name"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">{t('workers.email')} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="worker-email"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">{t('workers.password')} *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                minLength={6}
                data-testid="worker-password"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">{t('workers.phone')}</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                data-testid="worker-phone"
              />
            </div>

            {/* Color picker */}
            <div className="space-y-2">
              <Label htmlFor="color">{t('workers.color')}</Label>
              <div className="flex gap-2">
                <Input
                  id="color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  className="w-14 h-10 p-1 cursor-pointer"
                  data-testid="worker-color"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  className="flex-1 font-mono text-sm"
                />
              </div>
            </div>

            {/* Payment Type Selection */}
            <div className="space-y-3">
              <Label>{t('workers.paymentType')}</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({...formData, payment_type: 'hourly'})}
                  className={cn(
                    "p-3 rounded-lg border text-left transition-all",
                    formData.payment_type === 'hourly' 
                      ? "border-accent bg-accent/10 ring-1 ring-accent" 
                      : "border-border hover:border-accent/50"
                  )}
                >
                  <p className="font-medium text-sm">{t('workers.hourlyPayment')}</p>
                  <p className="text-xs text-muted-foreground">{t('workers.hourlyPaymentDesc')}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, payment_type: 'percentage'})}
                  className={cn(
                    "p-3 rounded-lg border text-left transition-all",
                    formData.payment_type === 'percentage' 
                      ? "border-accent bg-accent/10 ring-1 ring-accent" 
                      : "border-border hover:border-accent/50"
                  )}
                >
                  <p className="font-medium text-sm">{t('workers.percentagePayment')}</p>
                  <p className="text-xs text-muted-foreground">{t('workers.percentagePaymentDesc')}</p>
                </button>
              </div>
            </div>

            {/* Rate Input */}
            {formData.payment_type === 'hourly' ? (
              <div className="space-y-2">
                <Label htmlFor="hourly_rate">{t('workers.hourlyRate')}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="hourly_rate"
                    type="number"
                    step="0.01"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                    placeholder="0.00"
                    className="pl-7"
                    data-testid="worker-rate"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">/ {t('time.hours')}</span>
                </div>
                {formData.hourly_rate && parseFloat(formData.hourly_rate) < 17.40 && (
                  <p className="text-xs text-amber-500 flex items-center gap-1">
                    ⚠️ Ниже минимальной ставки BC 2025 ($17.40/ч)
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="percentage_rate">{t('workers.percentageRate')}</Label>
                <div className="relative">
                  <Input
                    id="percentage_rate"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.percentage_rate}
                    onChange={(e) => setFormData({...formData, percentage_rate: e.target.value})}
                    placeholder="10"
                    className="pr-8"
                    data-testid="worker-percentage"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground">{t('workers.percentageNote')}</p>
              </div>
            )}

            {/* CIN Number */}
            <div className="space-y-2">
              <Label htmlFor="cin_number">{t('workers.cinNumber')}</Label>
              <Input
                id="cin_number"
                value={formData.cin_number}
                onChange={(e) => setFormData({...formData, cin_number: e.target.value})}
                placeholder="CIN123456"
                data-testid="worker-cin"
              />
            </div>

            {/* Home Address */}
            <div className="space-y-2">
              <Label htmlFor="home_address">{t('workers.homeAddress')}</Label>
              <Input
                id="home_address"
                value={formData.home_address}
                onChange={(e) => setFormData({...formData, home_address: e.target.value})}
                placeholder={t('workers.homeAddressPlaceholder')}
                data-testid="worker-address"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading} data-testid="worker-submit">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              {t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
